/**
 * Instruction provider.
 *
 * Manages instruction registration from components throughout the tree.
 * Uses Immer for reference stability to prevent unnecessary re-renders.
 */

import type { ReactNode } from 'react';
import { useImmer } from 'use-immer';

import { InstructionContext } from '#plugin-chat/contexts/InstructionContext.ts';
import type { InstructionContextType, Placement, RenderedBlock } from '#plugin-chat/lib/instructions/types.ts';

/**
 * Provider that manages instruction registration.
 *
 * Components can register instructions via useContext(InstructionContext).registerInstruction().
 * Instructions are collected and sorted by priority when accessed via getInstructions().
 */
export function InstructionProvider({ children }: { children: ReactNode }) {
  const [instructions, setInstructions] = useImmer<RenderedBlock[]>([]);

  const contextValue: InstructionContextType = {
    registerInstruction(block) {
      // Add block to collection
      setInstructions((draft) => {
        draft.push(block);
      });

      // Return cleanup function to remove on unmount
      return () => {
        setInstructions((draft) => {
          const index = draft.findIndex(
            (b) => b.content === block.content && b.priority === block.priority && b.placement === block.placement,
          );
          if (index !== -1) {
            draft.splice(index, 1);
          }
        });
      };
    },

    getInstructions(placement?: Placement) {
      const sorted = instructions.toSorted((a, b) => a.priority - b.priority);
      if (placement) {
        return sorted.filter((s) => s.placement === placement);
      }
      return sorted;
    },
  };

  return <InstructionContext value={contextValue}>{children}</InstructionContext>;
}
